import React, { useState, useEffect } from "react";
import {
	AppBar,
	Box,
	Toolbar,
	IconButton,
	Typography,
	Container,
	Button,
	Drawer,
	List,
	ListItem,
	Divider,
	useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTheme } from "@mui/material/styles";
import { colors } from "../../Theme/theme";
import { FIRM } from "../../constants/firm";

const pages = [
	{ name: "Contact Us", id: "Contact" },
	{ name: "Our Practice", id: "Practice" },
	{ name: "The Team", id: "About" },
];

const StyledAppBar = styled(AppBar, {
	shouldForwardProp: (prop) => prop !== "scrolled",
})(({ scrolled }) => ({
	backgroundColor: "#f5f6f8",
	height: scrolled ? "72px" : "84px",
	transition: "height 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
	position: "fixed",
	zIndex: 1100,
	boxShadow: scrolled
		? "0 2px 16px rgba(26, 71, 137, 0.1)"
		: "none",
	borderBottom: scrolled
		? "1px solid transparent"
		: `1px solid rgba(26, 71, 137, 0.1)`,
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
}));

const NavButton = styled(Button)(() => ({
	color: colors.primary.dark,
	fontWeight: 500,
	letterSpacing: "0.02em",
	position: "relative",
	transition: "color 0.2s ease",
	"&:hover": {
		backgroundColor: "transparent",
		color: colors.primary.main,
		"&::after": {
			width: "100%",
		},
	},
	"&::after": {
		content: '""',
		position: "absolute",
		bottom: "8px",
		left: 0,
		width: 0,
		height: "2px",
		backgroundColor: colors.accent.main,
		transition: "width 0.25s ease",
	},
}));

const PhoneButton = styled(Button)(() => ({
	backgroundColor: colors.accent.main,
	color: colors.primary.darker,
	fontWeight: 700,
	padding: "8px 18px",
	borderRadius: 0,
	fontSize: "0.95rem",
	letterSpacing: "0.02em",
	transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
	"&:hover": {
		backgroundColor: colors.accent.dark,
		transform: "translateY(-1px)",
		boxShadow: "0 4px 12px rgba(250, 212, 97, 0.4)",
	},
}));

const MobileDrawer = styled(Drawer)(() => ({
	"& .MuiDrawer-paper": {
		width: "100%",
		maxWidth: "320px",
		background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.darker} 100%)`,
		color: "#ffffff",
		borderRadius: 0,
	},
}));

const DrawerButton = styled(Button)(() => ({
	width: "100%",
	justifyContent: "flex-start",
	padding: "12px 24px",
	color: "#ffffff",
	borderLeft: "3px solid transparent",
	borderRadius: 0,
	fontSize: "1rem",
	fontWeight: 500,
	letterSpacing: "0.02em",
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: "rgba(255, 255, 255, 0.08)",
		borderLeft: `3px solid ${colors.accent.main}`,
	},
}));

const DrawerPhoneButton = styled(Button)(() => ({
	backgroundColor: colors.accent.main,
	color: colors.primary.darker,
	margin: "8px 24px",
	padding: "10px 20px",
	borderRadius: 0,
	fontWeight: 700,
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: colors.accent.dark,
	},
}));

const NavBar = () => {
	const [scrolled, setScrolled] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const isLarge = useMediaQuery(theme.breakpoints.up("xl"));

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 30);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const toggleDrawer = (open) => (event) => {
		if (event?.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
		setDrawerOpen(open);
	};

	const drawerContent = (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box
					component="a"
					href="#"
					onClick={(e) => {
						e.preventDefault();
						window.scrollTo({ top: 0, behavior: "smooth" });
						setDrawerOpen(false);
					}}
					sx={{ textDecoration: "none" }}
				>
					<Box
						component="img"
						src="/DRLawLogo2025.png"
						alt="David Rhee Injury Law"
						sx={{
							height: "45px",
							filter: "brightness(0) invert(1)",
						}}
					/>
				</Box>
				<IconButton onClick={toggleDrawer(false)} sx={{ color: "#ffffff" }}>
					<CloseIcon />
				</IconButton>
			</Box>

			<Divider sx={{ backgroundColor: "rgba(255,255,255,0.1)", my: 1 }} />

			<List sx={{ flexGrow: 1 }}>
				{pages.map((page) => (
					<ListItem key={page.name} disablePadding>
						<DrawerButton href={`#${page.id}`} onClick={toggleDrawer(false)}>
							{page.name}
						</DrawerButton>
					</ListItem>
				))}
			</List>

			<Box sx={{ mt: "auto" }}>
				<Divider sx={{ backgroundColor: "rgba(255,255,255,0.1)", mb: 2 }} />
				<Typography
					variant="caption"
					sx={{ px: 3, mb: 1.5, display: "block", color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.65rem" }}
				>
					Hablamos Español
				</Typography>
				<DrawerPhoneButton startIcon={<PhoneIcon />} href={FIRM.phone.href}>
					{FIRM.phone.display}
				</DrawerPhoneButton>
				<Box sx={{ pb: 3 }} />
			</Box>
		</Box>
	);

	return (
		<StyledAppBar position="fixed" elevation={0} scrolled={scrolled ? 1 : 0}>
			<Container maxWidth="xl">
				<Toolbar disableGutters sx={{ justifyContent: "space-between", height: "100%" }}>
					{/* Mobile menu button */}
					{isMobile && (
						<IconButton
							size="large"
							aria-label="open navigation menu"
							onClick={toggleDrawer(true)}
							sx={{ color: colors.primary.main }}
						>
							<MenuIcon />
						</IconButton>
					)}

					{/* Logo */}
					{!isMobile && (
						<Box
							component="a"
							href="#"
							onClick={(e) => {
								e.preventDefault();
								window.scrollTo({ top: 0, behavior: "smooth" });
							}}
							sx={{
								textDecoration: "none",
								display: "flex",
								alignItems: "center",
								transition: "opacity 0.2s ease",
								"&:hover": { opacity: 0.8 },
							}}
						>
							<Box
								component="img"
								src="/DRLawLogo2025.png"
								alt="David Rhee Injury Law"
								sx={{
									height: scrolled ? "52px" : "62px",
									transition: "height 0.3s ease",
								}}
							/>
						</Box>
					)}

					{/* Desktop nav links */}
					{!isMobile && (
						<Box sx={{ display: "flex", ml: isLarge ? 8 : 4, flexGrow: 1 }}>
							{pages.map((page) => (
								<NavButton key={page.name} href={`#${page.id}`}>
									<Typography variant="button" sx={{ fontSize: "1rem", fontWeight: 500 }}>
										{page.name}
									</Typography>
								</NavButton>
							))}
						</Box>
					)}

					{/* Phone CTA */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						{!isMobile && (
							<Typography
								variant="button"
								sx={{
									color: colors.primary.main,
									fontWeight: 500,
									fontSize: "0.95rem",
									letterSpacing: "0.05em",
								}}
							>
								Free Consultation
							</Typography>
						)}
						<PhoneButton
							variant="contained"
							href={FIRM.phone.href}
							startIcon={isMobile ? <PhoneIcon /> : null}
							size={isMobile ? "small" : "medium"}
						>
							{FIRM.phone.display}
						</PhoneButton>
					</Box>
				</Toolbar>
			</Container>

			{/* Mobile drawer */}
			<MobileDrawer
				anchor="left"
				open={drawerOpen}
				onClose={toggleDrawer(false)}
				ModalProps={{ keepMounted: true }}
			>
				{drawerContent}
			</MobileDrawer>
		</StyledAppBar>
	);
};

export default NavBar;
